import {
  ReactEventHandler,
  FC,
  useState,
  useEffect,
  SyntheticEvent,
} from 'react';
import Button from '@material-ui/core/Button';
import {
  makeStyles,
  createStyles,
  Theme,
  MuiThemeProvider,
  createTheme,
} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import { Box, Typography } from '@material-ui/core';
import { Request } from 'types/RequestDataInfo';
import MessagePanel from './MessagePanel';
import CustomTextField2 from './CustomTextField2';

const theme1 = createTheme({
  overrides: {
    MuiFormControl: {
      marginDense: {
        marginTop: 1,
        marginBottom: 1,
      },
    },
  },
});

const theme2 = createTheme({
  overrides: {
    MuiButton: {
      containedPrimary: {
        backgroundColor: 'gray',
      },
    },
    MuiDialogActions: {
      root: {
        justifyContent: 'left',
      },
    },
  },
});

const theme3 = createTheme({
  overrides: {
    MuiButton: {
      containedPrimary: {
        backgroundColor: '#2196f3',
      },
    },
  },
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      margin: theme.spacing(2, 5, 0),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'left',
      fontSize: '18px',
    },
    color: {
      backgroundColor: '#87cefa',
    },
    colorRequired: {
      color: 'red',
      alignItems: 'right',
    },
    form: {
      width: '100%',
    },
    configuration: {
      margin: theme.spacing(0, 3, 0),
      '&:hover': {
        background: '#1769aa',
      },
    },
    return: {
      margin: theme.spacing(0, 5, 0),
      '&:hover': {
        background: 'darkgray',
      },
    },
    dialogTitle: {
      fontSize: 24,
    },
    width: {
      width: 220,
    },
    length: {
      width: 500,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(1, 0),
    },
    label: {
      width: 160,
      flexShrink: 0,
    },
    field: {
      width: 400,
    },
    titleSpace: {
      margin: theme.spacing(3, 0, 1),
    },
    tableSpace: {
      marginBottom: theme.spacing(4),
    },
  }),
);

export type InvoiceMailAddressDialogProps = {
  open: boolean;
  onClickClose: ReactEventHandler<HTMLButtonElement>;
  requestData?: Request;
  updateRequest?: (value: Request) => void;
};

const InvoiceMailAddressDialog: FC<InvoiceMailAddressDialogProps> = ({
  open,
  onClickClose,
  requestData,
  updateRequest,
}) => {
  const classes = useStyles();
  const mailCheck = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const [tempRequestData, setTempRequestData] = useState<Request>();
  const [errorMessages, setErrorMessages] = useState<string[]>(['']);
  const [isHide, setIsHide] = useState<boolean>(true);

  useEffect(() => {
    if (requestData) {
      setTempRequestData(requestData);
    }
  }, [requestData]);

  // 設定ボタン
  const confirmation = (event: SyntheticEvent<HTMLButtonElement>) => {
    if (tempRequestData) {
      let error = false;
      const message: string[] = [];
      if (!tempRequestData.chargeMailSendName) {
        message.push('・名称を入力してください。');
        error = true;
      }
      if (!tempRequestData.chargeMailSendMail) {
        message.push('・メールアドレスを入力してください。');
        error = true;
      }
      if (
        tempRequestData.chargeMailSendMail &&
        !mailCheck.test(tempRequestData.chargeMailSendMail)
      ) {
        message.push('・メールアドレスの形式が正しくありません。');
        error = true;
      }
      if (!tempRequestData.chargeMailSendPic) {
        message.push('・担当者を入力してください。');
        error = true;
      }
      if (error) {
        setIsHide(false);
        setErrorMessages(message);

        return;
      }
      setIsHide(true);
      if (updateRequest) updateRequest(tempRequestData);
      onClickClose(event);
    }
  };

  // ダイアログを閉じる
  const dialogClose = (event: SyntheticEvent<HTMLButtonElement>) => {
    setIsHide(true);
    setErrorMessages(['']);
    onClickClose(event);
  };

  return (
    <>
      <Dialog maxWidth="md" open={open} onClose={dialogClose}>
        <DialogTitle className={classes.color}>
          <Box className={classes.dialogTitle}>送付先設定（請求書メール）</Box>
        </DialogTitle>
        <DialogContent>
          <div className={classes.paper}>
            <div className={classes.form}>
              <MuiThemeProvider theme={theme2}>
                <DialogActions>
                  <MuiThemeProvider theme={theme3}>
                    <Button
                      variant="contained"
                      onClick={confirmation}
                      color="primary"
                      className={classes.configuration}
                    >
                      設定
                    </Button>
                  </MuiThemeProvider>
                  <Button
                    variant="contained"
                    onClick={dialogClose}
                    color="primary"
                    className={classes.return}
                  >
                    戻る
                  </Button>
                </DialogActions>
              </MuiThemeProvider>
              <MessagePanel messages={errorMessages} isHide={isHide} />
              <Typography
                component="h1"
                variant="h5"
                className={classes.titleSpace}
              >
                送付先
              </Typography>

              <MuiThemeProvider theme={theme1}>
                <table className={`destination-input ${classes.tableSpace}`}>
                  <tbody>
                    <tr>
                      <th className={classes.width}>
                        名称
                        <span
                          style={{ float: 'right' }}
                          className={classes.colorRequired}
                        >
                          (必須)
                        </span>
                      </th>
                      <td className={classes.length}>
                        <CustomTextField2
                          variant="outlined"
                          margin="dense"
                          required
                          fullWidth
                          id="chargeMailSendName"
                          name="chargeMailSendName"
                          autoComplete="chargeMailSendName"
                          value={tempRequestData?.chargeMailSendName ?? ''}
                          onChange={(e) => {
                            setTempRequestData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    chargeMailSendName: e.target.value,
                                  }
                                : prev,
                            );
                          }}
                          inputProps={{
                            maxLength: 50,
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        メールアドレス
                        <span
                          style={{ float: 'right' }}
                          className={classes.colorRequired}
                        >
                          (必須)
                        </span>
                      </th>
                      <td>
                        <CustomTextField2
                          variant="outlined"
                          margin="dense"
                          required
                          fullWidth
                          id="chargeMailSendMail"
                          name="chargeMailSendMail"
                          autoComplete="chargeMailSendMail"
                          value={tempRequestData?.chargeMailSendMail ?? ''}
                          onChange={(e) => {
                            setTempRequestData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    chargeMailSendMail: e.target.value,
                                  }
                                : prev,
                            );
                          }}
                          inputProps={{ maxLength: 50 }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        担当者
                        <span
                          style={{ float: 'right' }}
                          className={classes.colorRequired}
                        >
                          (必須)
                        </span>
                      </th>
                      <td>
                        <CustomTextField2
                          variant="outlined"
                          margin="dense"
                          required
                          fullWidth
                          id="chargeMailSendPic"
                          name="chargeMailSendPic"
                          autoComplete="chargeMailSendPic"
                          value={tempRequestData?.chargeMailSendPic ?? ''}
                          onChange={(e) => {
                            setTempRequestData((prev) =>
                              prev
                                ? { ...prev, chargeMailSendPic: e.target.value }
                                : prev,
                            );
                          }}
                          inputProps={{ maxLength: 50 }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </MuiThemeProvider>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvoiceMailAddressDialog;
